import {
  IServerSideGroupSelectionState,
  SortModelItem
} from 'ag-grid-community';
import { utils, write } from 'xlsx';
import {
  TableHeaderFiltersType,
  TotalsFooterType
} from 'components/ComposedTable/types';
import { LockStatus } from 'pages/AuthorizePaymentPage/types';
import { PrintChecksVendorData } from 'pages/VendorsTablePage/components/PrintChecks/types';
import {
  getNumberFromCurrency,
  getVendorsTableDataId
} from 'pages/VendorsTablePage/components/VendorsTable/helpers';
import {
  VendorDataType,
  VendorsTableDataType
} from 'pages/VendorsTablePage/components/VendorsTable/types';
import { formatCurrency } from 'utils';

export const getSortedInvoiceRows =
  (sortColumnData: SortModelItem) =>
  (rowA: VendorsTableDataType, rowB: VendorsTableDataType): number => {
    const valA = rowA[sortColumnData.colId as keyof VendorsTableDataType];
    const valB = rowB[sortColumnData.colId as keyof VendorsTableDataType];
    if (!valA || !valB) return 0;
    if (valA < valB) {
      return sortColumnData.sort === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return sortColumnData.sort === 'asc' ? 1 : -1;
    }
    return 0;
  };

export const getAllSelectableInvoices = (
  vendorsTableData: VendorsTableDataType[]
): VendorsTableDataType[] =>
  vendorsTableData?.flatMap(currentVendor =>
    currentVendor?.invoices
      .filter(
        invoice =>
          !invoice?.onHold &&
          getNumberFromCurrency(invoice?.netAmount) > 0 &&
          invoice?.lockStatus !== LockStatus.LOCKED
      )
      .map(invoiceData => ({
        ...currentVendor,
        ...invoiceData,
        id: getVendorsTableDataId(currentVendor, invoiceData),
        invoices: []
      }))
  );

export const generateExcelDocument = (
  gridData: VendorDataType[],
  totalsInFooter: TotalsFooterType | undefined,
  tableHeaderFilters: TableHeaderFiltersType | undefined
): Blob => {
  const headerData = [
    [
      `Title: ${tableHeaderFilters?.tableTitle}`,
      ' ',
      ' ',
      ' ',
      ' ',
      ' ',
      ' ',
      ' ',
      ' ',
      ' '
    ],
    [`Store: ${tableHeaderFilters?.selectedStore}`],
    [`Due On or Before: ${tableHeaderFilters?.selectedDueDate}`],
    [`Payment Method: ${tableHeaderFilters?.selectedPaymentMethods}`]
  ];
  const dataRows = gridData.flatMap(vendor => [
    {
      'Vendor No.': vendor.vendorNo,
      'Vendor Name': vendor.vendorName,
      'Payment Method': vendor.paymentMethod,
      'Invoice No.': '',
      'Amount ($)': vendor.totalAmount,
      'Discount ($)': vendor.totalDiscountAmount,
      'Net Payment ($)': vendor.totalNetAmount,
      'Invoice Date': '',
      'Due Date': '',
      Hold: ''
    },
    ...vendor.invoices.map(invoice => ({
      'Vendor No.': vendor.vendorNo,
      'Vendor Name': vendor.vendorName,
      'Payment Method': vendor.paymentMethod,
      'Invoice No.': invoice.invoiceId,
      'Amount ($)': invoice.amount,
      'Discount ($)': invoice.discountAmount,
      'Net Payment ($)': invoice.netAmount,
      'Invoice Date': invoice.invoiceDate,
      'Due Date': invoice.dueDate,
      Hold: invoice.onHold ? 'Yes' : 'No'
    }))
  ]);
  const worksheet = utils.json_to_sheet(headerData, {
    skipHeader: true
  });
  utils.sheet_add_json(worksheet, dataRows, {
    origin: 'A6'
  });
  utils.sheet_add_json(
    worksheet,
    [
      [`Discount: ${formatCurrency(totalsInFooter?.discountTotalInFooter)}`],
      [`Total Owed: ${formatCurrency(totalsInFooter?.totalOwedInFooter)}`]
    ],
    {
      origin: `A${dataRows.length + 8}`,
      skipHeader: true
    }
  );
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, 'Export');
  const array = write(workbook, {
    bookType: 'xls',
    compression: true,
    type: 'array'
  }) as ArrayBuffer;
  return new Blob([new Uint8Array(array)], {
    type: 'application/octet-stream'
  });
};

export const getServerSideSelectedRows = (
  gridRowData: PrintChecksVendorData[] | undefined,
  selectedRowsIds: string[] | IServerSideGroupSelectionState[] | undefined
): PrintChecksVendorData[] => {
  const rowMap = new Map();
  gridRowData
    ?.filter(item1 =>
      selectedRowsIds?.some(rows => {
        if (typeof rows !== 'string') {
          return rows?.nodeId === item1.vendorNo;
        }
        return false;
      })
    )
    .forEach(obj => {
      rowMap.set(obj?.vendorNo, obj);
    });

  return Array.from(rowMap.values());
};
